/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useRef } from 'react';

import { jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';

import type { FieldBaseProps } from '../common/types';

import type { JiraUrlField_Platform$key } from './__generated__/JiraUrlField_Platform.graphql';
import { CustomTextField, type CustomTextFieldProps } from './CustomTextField';

export type Props = FieldBaseProps & {
	field: JiraUrlField_Platform$key;
	onChange: CustomTextFieldProps['onChange'];
};

export const JiraUrlField = ({
	fieldValue,
	field,
	isFieldEditable = true,
	onChange,
}: Props): JSX.Element | null => {
	const data = useFragment<JiraUrlField_Platform$key>(
		graphql`
			fragment JiraUrlField_Platform on JiraUrlField {
				name
				fieldConfig {
					isEditable
					isRequired
				}
				uri
			}
		`,
		field,
	);

	const isEditable = (data?.fieldConfig?.isEditable ?? false) && isFieldEditable;
	const isRequired = !!data?.fieldConfig?.isRequired;
	const defaultValue = typeof data?.uri === 'string' ? data.uri : '';
	const currentValue = typeof fieldValue === 'string' ? fieldValue : '';

	// Allows us to make sure we only fall back to default value once, on initial render
	const isFirstRenderRef = useRef(true);
	useEffect(() => {
		// If the passed down value is empty and a default value is available, tell the upstream
		if (fieldValue == null && defaultValue !== '' && isFirstRenderRef.current) {
			onChange(defaultValue);
		}
		isFirstRenderRef.current = false;
	}, [fieldValue, onChange, defaultValue]);

	const handleChange = useCallback(
		(newValue: string | null) => {
			onChange(newValue === '' ? null : newValue);
		},
		[onChange],
	);

	return (
		<CustomTextField
			isEditable={!!isEditable}
			isRequired={!!isRequired}
			currentValue={currentValue}
			fieldName={data?.name}
			onChange={handleChange}
			inputType="url"
		/>
	);
};
